import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CustomViewsAPI from '../../api/customViews';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
  },
  fetchedFilterType: null,
  activeConversationFolder: null,
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getFetchedFilterType(_state) {
    return _state.fetchedFilterType;
  },
  getCustomViews(_state) {
    return _state.records;
  },
  getCustomViewsByFilterType: _state => filterType => {
    return _state.records.filter(record => record.filter_type === filterType);
  },
  getActiveConversationFolder(_state) {
    return _state.activeConversationFolder;
  },
};

let fetchRequestId = 0;

export const actions = {
  get: async function getCustomViews(
    { commit, state: currentState },
    filterType
  ) {
    fetchRequestId += 1;
    const requestId = fetchRequestId;
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isFetching: true });
    if (currentState.fetchedFilterType !== filterType) {
      commit(types.RESET_CUSTOM_VIEW_FETCH_STATUS);
    }
    try {
      const response =
        await CustomViewsAPI.getCustomViewsByFilterType(filterType);
      if (requestId === fetchRequestId) {
        commit(types.SET_CUSTOM_VIEW, { data: response.data, filterType });
      }
    } catch (error) {
      // Ignore error
    } finally {
      if (requestId === fetchRequestId) {
        commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isFetching: false });
      }
    }
  },
  create: async function createCustomViews({ commit }, obj) {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: true });
    try {
      const response = await CustomViewsAPI.create(obj);
      const created = response.data;
      commit(types.SET_SINGLE_CUSTOM_VIEW, created);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: false });
    }
  },
  update: async function updateCustomViews({ commit }, payload) {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: true });
    try {
      const id = payload.id;
      const response = await CustomViewsAPI.update(id, payload);
      const updated = response.data;
      commit(types.SET_SINGLE_CUSTOM_VIEW, updated);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: false });
    }
  },
  delete: async ({ commit }, { id, filterType }) => {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isDeleting: true });
    try {
      await CustomViewsAPI.deleteCustomViews(id, filterType);
      commit(types.DELETE_CUSTOM_VIEW, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isDeleting: false });
    }
  },
  setActiveConversationFolder({ commit }, data) {
    commit(types.SET_ACTIVE_CONVERSATION_FOLDER, data);
  },
};

export const mutations = {
  [types.SET_CUSTOM_VIEW_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CUSTOM_VIEW]: MutationHelpers.create,
  [types.SET_SINGLE_CUSTOM_VIEW]: MutationHelpers.setSingleRecord,
  [types.SET_CUSTOM_VIEW](_state, { data, filterType }) {
    MutationHelpers.set(_state, data);
    _state.fetchedFilterType = filterType;
  },
  [types.RESET_CUSTOM_VIEW_FETCH_STATUS](_state) {
    _state.fetchedFilterType = null;
  },
  [types.UPDATE_CUSTOM_VIEW]: MutationHelpers.update,
  [types.DELETE_CUSTOM_VIEW]: MutationHelpers.destroy,

  [types.SET_ACTIVE_CONVERSATION_FOLDER](_state, folder) {
    _state.activeConversationFolder = folder;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
